/**
 * @file components/log/WeightTracker.jsx
 * @description Hook-based weight tracking dashboard.
 */

import { useQueryClient } from "@tanstack/react-query";
import { History, Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { trackWeightJourneyLogged } from "../../services/analytics";
import { updateProfileData } from "../../services/firebase";
import WeightLogModal from "./WeightLogModal";
import WeightProgressCharts from "./WeightProgressCharts";
import { WeeklyReviewModal } from "../shared/WeeklyReviewModal";

// Hooks
import { useMeals } from "../../hooks/useMeals";
import { useProfile } from "../../hooks/useProfile";
import { useWeights } from "../../hooks/useWeight";
import { useWeightMutations } from "../../hooks/useWeightMutations";
import { useWeeklyReview } from "../../hooks/useWeeklyReview";
import { useAuth } from "../../utils/AuthContext";

export default function WeightTracker({ setOpenVitalsModal, onShowAuth }) {
  const { uid, isGuest } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();

  // 1. Data Fetching via Hooks
  const { data: profileData = {} } = useProfile(uid);
  const { data: meals = [] } = useMeals(uid);
  const { data: weightLogs = [], isLoading: weightsLoading } = useWeights(uid);

  // 2. Mutations
  const { addWeightMutation, deleteWeightMutation } = useWeightMutations(uid);

  // Weekly deficit recap (once per completed week)
  const { reviewOpen, week, insights, dismissReview } = useWeeklyReview(
    meals,
    profileData,
  );

  // Guest guard (all hooks above run unconditionally to preserve hook order)
  if (isGuest) {
    return (
      <div className="text-center py-20 px-6 bg-white/[0.02] rounded-3xl border border-dashed border-white/10">
        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
          <Plus className="text-gray-600" size={24} />
        </div>
        <h3 className="text-white font-bold text-lg">Track your weight</h3>
        <p className="text-gray-500 text-sm mt-1 mb-5">
          Sign in to log weight entries and see your progress over time.
        </p>
        <button
          onClick={onShowAuth}
          className="px-8 py-3 rounded-xl bg-brand-green text-brand-bg font-bold text-xs uppercase tracking-widest hover:bg-green-400 transition-colors"
        >
          Sign in to start
        </button>
      </div>
    );
  }

  // 3. Handlers
  const handleSaveWeight = async (weight, date, notes) => {
    addWeightMutation.mutate(
      { weight, date, notes, createdAt: new Date() },
      {
        onSuccess: async () => {
          trackWeightJourneyLogged(uid, weight, date);

          // Profile Sync Logic
          const today = new Date();
          const logDate = new Date(date);
          if (
            today.getFullYear() === logDate.getFullYear() &&
            today.getMonth() === logDate.getMonth() &&
            today.getDate() === logDate.getDate()
          ) {
            await updateProfileData(uid, { weight });
            queryClient.invalidateQueries({ queryKey: ["profile", uid] });
          }

          setShowModal(false);
        },
      },
    );
  };

  const handleDeleteWeight = async (logId) => {
    const deletedWeight = weightLogs.find((log) => log.id === logId);
    const deletedDate = deletedWeight ? new Date(deletedWeight.date) : null;

    deleteWeightMutation.mutate(logId, {
      onSuccess: async () => {
        // Find what the remaining logs will be locally to avoid awaiting another network request
        const remainingLogs = weightLogs.filter((l) => l.id !== logId);

        if (deletedDate) {
          if (remainingLogs.length > 0) {
            const mostRecentLog = remainingLogs.sort(
              (a, b) => new Date(b.date) - new Date(a.date),
            )[0];
            await updateProfileData(uid, { weight: mostRecentLog.weight });
          } else {
            setOpenVitalsModal(true);
          }
          queryClient.invalidateQueries({ queryKey: ["profile", uid] });
        }
      },
    });
  };

  return (
    <div className="space-y-8 pb-10 animate-in fade-in duration-700">
      {/* Header */}
      <div className="flex items-end justify-between px-2">
        <div>
          <h2 className="font-serif text-3xl text-white tracking-tight">
            Weight Progress
          </h2>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-2">
            Target:{" "}
            <span className="text-brand-green">
              {profileData.goalWeight || "--"} kg
            </span>
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-brand-green text-[#0F1115] text-xs font-black uppercase tracking-widest hover:bg-green-400 transition-all shadow-[0_0_20px_rgba(16,185,129,0.15)] active:scale-95"
        >
          <Plus size={16} strokeWidth={3} /> Log
        </button>
      </div>

      {/* Analytics Section */}
      {weightsLoading ? (
        <div className="h-[300px] w-full bg-white/[0.02] rounded-[2rem] animate-pulse border border-white/5" />
      ) : (
        <WeightProgressCharts
          weightLogs={weightLogs}
          meals={meals}
          profileData={profileData}
        />
      )}

      {/* History Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 px-2">
          <History size={14} className="text-gray-500" />
          <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
            Entry History
          </h3>
        </div>

        {weightLogs.length === 0 && !weightsLoading ? (
          <div className="text-center py-10 bg-white/[0.02] rounded-[2rem] border border-dashed border-white/10">
            <p className="text-xs text-gray-600 font-medium">
              Your journey starts with the first log.
            </p>
          </div>
        ) : (
          <div className="grid gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            {weightLogs
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .map((log) => {
                const date = new Date(log.date);
                const isDeletingThisLog =
                  deleteWeightMutation.isPending &&
                  deleteWeightMutation.variables === log.id;

                return (
                  <div
                    key={log.id}
                    className="group relative flex items-center justify-between p-5 rounded-3xl bg-white/[0.02] border border-white/5 hover:border-white/10 hover:bg-white/[0.04] transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-2xl bg-brand-green/10 flex items-center justify-center text-brand-green font-bold text-sm">
                        {log.weight.toString().split(".")[0]}
                      </div>
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl font-black text-white">
                            {log.weight}
                            <span className="text-[10px] ml-1 opacity-40">
                              kg
                            </span>
                          </span>
                          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter">
                            {date.toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                        {log.notes && (
                          <p className="text-[11px] text-gray-500 mt-0.5 italic">
                            &quot;{log.notes}&quot;
                          </p>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => handleDeleteWeight(log.id)}
                      disabled={isDeletingThisLog}
                      className="p-3 rounded-xl text-gray-600 hover:text-red-400 hover:bg-red-400/10 transition-all opacity-0 group-hover:opacity-100"
                    >
                      {isDeletingThisLog ? (
                        <Loader2
                          size={16}
                          className="animate-spin text-red-400"
                        />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      <WeightLogModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaveWeight}
        loading={addWeightMutation.isPending}
      />

      {reviewOpen && (
        <WeeklyReviewModal
          week={week}
          insights={insights}
          onDismiss={dismissReview}
        />
      )}
    </div>
  );
}
